import React, { useCallback, useEffect, useState } from 'react';

import TagManager from 'react-gtm-module';

import { LoadingType, PreQualificationStep, setPreQualificationStep } from 'actions';
import { setDashboardStep } from 'actions/stages';
import { useAppDispatch } from 'hooks/useAppDispatch';
import { useAppSelector } from 'hooks/useAppSelector';
import { getApplicationInfoForDashboardThunk } from 'thunks/getApplicationInfoForDashboard';
import * as notificationManager from 'notifications/index';
import { FailureNotificationType } from 'notifications/index';
import { OfferDto, OffersResponseDto } from 'api/AutomatedOffersApi';
import AcceptAutomatedOffer from 'pages/Dashboard/steps/AcceptOffer/AcceptAutomatedOffer/AcceptAutomatedOffer';
import { DashboardStep } from 'enums/DashboardStep';
import { clearApplication } from 'actions/application';
import { GOOGLE_TAG_MANAGER_ARGS } from 'constants/constants';
import { AppRoutes } from 'routes/routes.enums';
import useAppNavigate from 'hooks/useAppNavigate';
import { ApplicationStatus } from 'enums/ApplicationStatus';
import { posAutomatedOffersApi } from 'api/SmallLoanProgram/AutomatedOffersApi';

const AcceptPosAutomatedOffer = () => {
  const navigator = useAppNavigate();
  const dispatch = useAppDispatch();
  const isGlobalLoading = useAppSelector((state) => state.loading[LoadingType.GetData]);
  const [isLoading, setIsLoading] = useState(true);
  const { applicationId, variables, applicationStatus } = useAppSelector((state) => state.application);
  const { borrowerId } = useAppSelector((state) => state.borrower);
  const [loanOffers, setLoanOffers] = useState<OfferDto[]>([]);
  const [currentLoanOffer, setCurrentLoanOffer] = useState<OfferDto | null>(null);
  const [maxLoanAmount, setMaxLoanAmount] = useState<number>(0);
  const [minLoanAmount, setMinLoanAmount] = useState<number>(0);
  const [loanAmount, setLoanAmount] = useState<number>(variables!.loan_amount!);

  const updateLoanOffers = useCallback((loanOffersResponse: OffersResponseDto) => {
    setLoanOffers(loanOffersResponse.loanOffers);
    setMaxLoanAmount(loanOffersResponse.maxLoanAmount);
    setMinLoanAmount(loanOffersResponse.minLoanAmount);

    if (loanAmount > loanOffersResponse.maxLoanAmount) {
      setLoanAmount(loanOffersResponse.maxLoanAmount);
    }
  }, []);

  const handleApplicationRejected = () => {
    TagManager.dataLayer(GOOGLE_TAG_MANAGER_ARGS.applicationDecline);
    dispatch(setPreQualificationStep(PreQualificationStep.NoOptions));
    navigator(AppRoutes.Prequalification);
  };

  const getLoanOffers = useCallback(async () => {
    try {
      setIsLoading(true);

      const loanOffersResponse = await posAutomatedOffersApi.getOffers({
        applicationId: applicationId!,
        loanAmount: loanAmount,
      });

      if (loanOffersResponse.isApplicationRejected) {
        handleApplicationRejected();
      } else {
        updateLoanOffers(loanOffersResponse);
      }
    } catch (error) {
      notificationManager.notifyFailure(FailureNotificationType.UnknownError);
    } finally {
      setIsLoading(false);
    }
  }, [applicationId, dispatch, loanAmount, updateLoanOffers]);

  useEffect(() => {
    if (applicationStatus === ApplicationStatus.Pitched) {
      getLoanOffers();
    }
  }, [applicationStatus, getLoanOffers, loanAmount]);

  const handleDeclineOffer = async () => {
    let isSuccess = false;

    try {
      setIsLoading(true);
      await posAutomatedOffersApi.declineOffer({
        applicationId: applicationId!,
      });

      isSuccess = true;
    } catch (error) {
      notificationManager.notifyFailure(FailureNotificationType.UnknownError);
    } finally {
      setIsLoading(false);
    }

    return isSuccess;
  };

  const handleCloseDeclineOfferThanksPopup = async () => {
    dispatch(clearApplication());
    dispatch(setDashboardStep(DashboardStep.NoActiveApplication));
  };

  const handleAcceptOffer = async () => {
    try {
      setIsLoading(true);

      const result = await posAutomatedOffersApi.acceptOffer({
        applicationId: applicationId!,
        loanAmount: loanAmount,
        term: currentLoanOffer!.term,
        lender: currentLoanOffer!.lender,
      });

      if (result.isApplicationRejected) {
        handleApplicationRejected();
      } else {
        if (currentLoanOffer!.offerUrl) {
          window.open(currentLoanOffer!.offerUrl, '_self');
        } else {
          await dispatch(getApplicationInfoForDashboardThunk(borrowerId!)).unwrap();
          dispatch(setDashboardStep(null));
        }
      }
    } catch (error: any) {
      notificationManager.notifyFailure(FailureNotificationType.UnknownError);
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <>
      <AcceptAutomatedOffer
        loanAmount={loanAmount}
        loanOffers={loanOffers}
        setLoanAmount={setLoanAmount}
        minLoanAmount={minLoanAmount}
        maxLoanAmount={maxLoanAmount}
        currentLoanOffer={currentLoanOffer}
        setCurrentLoanOffer={setCurrentLoanOffer}
        onCloseDeclineOfferThanksPopup={handleCloseDeclineOfferThanksPopup}
        onDeclineOffer={handleDeclineOffer}
        onAcceptOffer={handleAcceptOffer}
        isLoading={isLoading || isGlobalLoading}
      />
    </>
  );
};

export default AcceptPosAutomatedOffer;
